// LiveStreamChatroomGift.jsx
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { TranslationNamespace } from '../resource/translationNamespace.js';
import defaults from '../resource/defaults.js';

import LiveStreamUserAvatar from '../container/LiveStreamUserAvatar.js';
import UserLevelBadge from '../container/UserLevelBadge.js';
import UserLivestreamBadges from '../container/UserLivestreamBadges.js';
import LiveStreamChatroomGiftItem from '../container/LiveStreamChatroomGiftItem.js';

import { USER_BADGE_SIZE } from '../component/UserLivestreamBadges.jsx';

import SlideInFromLeft from '../style/SlideInFromLeft.js';
import SlideOutToLeft from '../style/SlideOutToLeft.js';
import { UserLevelBadgeVersion } from '../component/UserLevelBadge.jsx';
const getGiftI18nKey = id => `giftname_${id.replace(/-/g, '_')}`;

export const LiveStreamChatroomGift = ({
  streamId,
  expensivePrice = 0,
  noticeStatus = 'hidden',
  giftId = '',
  senderId = '',
  senderLevel = null,
  senderName = '',
  giftPrice = 0,
  senderBadges = defaults.EMPTY_ARRAY,
  fetchUser = defaults.NULL_FUNCTION,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    let nextTick = null;
    const shouldfetchUser = giftId && !senderName;
    if (shouldfetchUser) {
      nextTick = setTimeout(() => {
        fetchUser({ userId: senderId });
      });
    }
    return () => {
      clearTimeout(nextTick);
    };
  }, [giftId, senderId, senderName, fetchUser]);

  const renderUserAvatar = () => (
    <AvatarInfo>
      <AvatarWrapper>
        <LiveStreamUserAvatar
          streamId={streamId}
          userId={senderId}
          badgeSize={44}
          avatarSize={28}
          diamondSize={10}
          fontSize={16}
          shouldDisplayMVP={true}
        />
      </AvatarWrapper>
    </AvatarInfo>
  );

  if (!giftId) return null;

  return (
    <StyledLiveStreamChatroomGift status={noticeStatus}>
      <GiftMessage highlight={giftPrice >= expensivePrice}>
        {renderUserAvatar()}
        <GiftInfo>
          <UserInfo>
            <Username>{senderName} </Username>
            <UserLevelBadge
              level={senderLevel}
              version={UserLevelBadgeVersion.normal}
            />
            <UserLivestreamBadges badges={senderBadges} />
          </UserInfo>
          <GiftRow>
            <SendText>
              {t('user_sent_gift', { ns: TranslationNamespace.GIFT })}
            </SendText>
            <Giftname>
              {t(
                getGiftI18nKey(giftId, {
                  ns: TranslationNamespace.GIFT,
                })
              )}
            </Giftname>
          </GiftRow>
        </GiftInfo>
        <LiveStreamChatroomGiftItem streamId={streamId} />
      </GiftMessage>
    </StyledLiveStreamChatroomGift>
  );
};

LiveStreamChatroomGift.propTypes = {
  streamId: PropTypes.string.isRequired,
  expensivePrice: PropTypes.number,
  noticeStatus: PropTypes.string,
  giftId: PropTypes.string,
  senderId: PropTypes.string,
  senderLevel: PropTypes.number,
  senderName: PropTypes.string,
  giftPrice: PropTypes.number,
  senderBadges: PropTypes.array,
  fetchUser: PropTypes.func,
};

const StyledLiveStreamChatroomGift = styled.div`
  display: inline-flex;
  align-items: center;
  margin-top: 16px;

  ${({ status }) => {
    if (status === 'coming') {
      return css`
        animation: 0.5s ${SlideInFromLeft};
      `;
    } else if (status === 'leaving') {
      return css`
        animation: 0.6s ${SlideOutToLeft};
      `;
    } else if (status === 'hidden') {
      return css`
        display: none;
      `;
    }
  }}
`;

const GiftMessage = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 4px 0;
  border-radius: 34px;
  position: relative;
  height: 40px;
  ${({ highlight }) =>
    highlight
      ? css`
          background: linear-gradient(
            102deg,
            rgba(0, 210, 190, 0.6),
            rgba(93, 130, 188, 0.6),
            52%,
            rgba(142, 55, 215, 0.6)
          );
        `
      : css`
          background-color: rgba(35, 35, 35, 0.6);
        `}
`;

const GiftInfo = styled.div`
  flex: 1;
  min-width: 70px;
  margin: 0 4px;
  color: #ffffff;
  text-align: left;
`;

const UserInfo = styled.div`
  display: flex;
  gap: 4px;
`;

const Username = styled.div`
  margin: 0;
  height: 14px;
  font-size: 12px;
  line-height: ${USER_BADGE_SIZE}px;
  color: #ffffff;
  :not(:first-child) {
    margin-left: 4px;
  }
`;

const Giftname = styled.div`
  height: 14px;
  font-size: 12px;
  line-height: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
`;

const AvatarInfo = styled.div`
  width: 44px;
  z-index: 0;
`;

const AvatarWrapper = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: center;
`;

const SendText = styled.div`
  font-size: 12px;
  font-weight: 400;
  line-height: 150%; /* 18px */
  letter-spacing: 0.012px;
`;

const GiftRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export default LiveStreamChatroomGift;
